// SINGLE SOURCE for OTE (MOM Overtime Exemption) quotation content — used by the office preview, the client signing
// page, the quotation email and the OTE terms page, so they can never disagree. Wording is the owner's own OTE
// quotation (HellermannTyton, Oct 2026), approved 3 Nov 2026. bizSAFE and Internal Audit quotes do NOT use this file.

export const OTE_TITLE = 'Endorsement of Overtime Exemption Scheme (OTE)'
export const OTE_DEFAULT_FEE = 1200          // S$ w/o GST — pre-filled on an empty OTE quote; the office can change it
export const OTE_DEFAULT_VALIDITY = 14       // days
export const OTE_SCOPE = 'On-site Audit and Endorsement of Risk Register of Work Activities undertaken during the overtime exemption period'
export const OTE_PAYMENT_TERMS = '100% upfront payment\nPayNow / GIRO preferred — UEN 199205611D\nNote: Endorsement will not be issued if payment is outstanding.'
export const OTE_REMARKS = [
  'Please ensure that your organisation has implemented Risk Management and have the required Risk Assessments for the audit. Please implement adequate control measures and ensure all RPN falls within the LOW category.',
  'The endorsement of Risk Register of Work Activities Undertaken does not guarantee an approval from MOM for your OTE application. You are encouraged to contact MOM to confirm that your work activity can be included in the exemption before engaging any auditing organisation.',
]

const remarksText = (quote) => [...OTE_REMARKS, ...(quote?.remarks ? [quote.remarks] : [])].map(s => '• ' + s).join('\n')

// [label, value] rows for the client signing page (fee row is rendered separately there).
export function oteQuoteRows(quote, client) {
  const r = quote?.form_responses || {}
  const businessNature = r.business_nature || client?.notes || client?.industry || ''
  return [
    ['Scope of service', OTE_SCOPE],
    businessNature ? ['Business nature', businessNature] : null,
    ['Payment terms', OTE_PAYMENT_TERMS],
    ['Remarks', remarksText(quote)],
  ].filter(Boolean)
}

// Lettered rows for the office preview (same layout as the owner's Word quotation; the terms refer to "Section BB").
export function oteQuotePreviewRows(quote, client, fmt) {
  const r = quote?.form_responses || {}
  const businessNature = r.business_nature || client?.notes || client?.industry || ''
  return [
    ['AA. FEES', `${fmt(quote?.fee_ex_gst)} w/o GST\n(${fmt(quote?.fee_inc_gst)} w/ 9% GST) per audit`],
    ['BB. SCOPE', OTE_SCOPE],
    ['CC. BUSINESS NATURE', businessNature || '—'],
    ['DD. PAYMENT TERMS', OTE_PAYMENT_TERMS],
    ['EE. QUOTE VALIDITY', `Good for ${quote?.validity_days || OTE_DEFAULT_VALIDITY} days only`],
    ['FF. REMARKS', remarksText(quote)],
  ]
}

// Client email. Returned as plain text for textEmailHtml (link on its own line → button).
export function oteQuoteEmail({ company, contact, quoteRef, link, validityDays }) {
  return {
    subject: `${company} - OTE Endorsement Quotation ${quoteRef}`,
    body: `Dear ${contact || 'Sir/Madam'},

Thank you for your interest in our Overtime Exemption (OTE) Risk Register Endorsement.

Please view and accept our quotation here:
${link}

This quotation is valid for ${validityDays || OTE_DEFAULT_VALIDITY} days. Feel free to contact us should you require any clarification, thank you.

Best regards,
CCIS Singapore Pte Ltd
(65) 6376 0550`,
  }
}

// Questionnaire email (office copies it into Outlook).
export function oteQuestionnaireEmail({ contact, link }) {
  return {
    subject: 'Overtime Exemption (OTE) Risk Register Endorsement — questionnaire',
    body: `Dear ${contact || 'Sir/Madam'},

Thank you for your interest in our Overtime Exemption (OTE) Risk Register Endorsement.

Please complete the questionnaire here:
${link}

Once submitted, we will prepare a quotation for your review within 1–2 business days.

Best regards,
CCIS Singapore Pte Ltd
(65) 6376 0550`,
  }
}

// The five clauses of the owner's OTE quotation (page 4), kept as written.
export const OTE_TERMS = [
  'Should the client request for additional on-site meetings outside of the Scope of service during the project period, a variation order of S$1,308.00 w/ GST shall be applied for each additional ½ man day required. ½ man day is defined as not more than 3.5 hours per session whereas 1 man day is defined as not more than 7 hours per session;',
  'For cancellation of any pre-agreed session by the company, an abortive charge of S$872 w/ GST shall be applied. The abortive charge is not applicable should the company notify CCIS of the postponement / changes at least 7 working days in advance from the agreed date of session;',
  'Should the project exceed twelve months after the contract signing date, a monthly charge of additional S$1,090 w/ GST shall be applied for each subsequent calendar month as retainer fee until successful certification is achieved;',
  'The company shall pay CCIS on-time and in strict accordance with the payment schedule. Late interest charge of 2% per month or part thereof shall be levied on any outstanding amount. No cancellation is allowed - The company shall compensate CCIS for loss or damage resulting from holding, suspending or cancellation of this service agreement. The company’s liability shall include (but is not limited to) fees for services that have been performed and 100% of the contractual sum for the services not yet performed.',
  'Any legal fees incurred by CCIS for recovery purpose shall be borne by the client, including a strictly non-waivable administrative fee of S$1,000 once we submit the case to The Small Claims Tribunals.',
]

// Annex A — MOM's "Work activities for which overtime exemption will not be granted", word for word (MOM PDF).
export const OTE_ANNEX_A = [
  { title: 'Any work where mental concentration is paramount to ensure safe operations:', items: [
    'Crane operators', 'Hoist and winch operators', 'Dredge operators', 'Excavating and loading machine',
    'Tank, truck and ship tanker loaders', 'Industrial truck, forklift and tractor operators',
    'Construction equipment operators (trench excavators, road graders and mechanical powered hand tools)', 'Scaffolders'] },
  { title: 'Any work involving continuous and manual operation of machinery. Examples:', items: [
    'Operators involved in manual mode operation of drilling, boring, milling, lathe, turning, cutting, punching, press, grinding, polishing, and buffing machines',
    'Operators involved in manual mode operation of extruding, drawing and rolling machines', 'Woodworking machine operators',
    'Manual blasting workers', 'Manual welding, soldering and brazing workers'] },
  { title: 'Work activities in extreme thermal conditions / involving continuous changes in working temperatures. Examples:', items: [
    'Iron, steel and nonferrous foundries', 'Brick-firing and ceramics operations', 'Glass products manufacturing plants',
    'Boiler rooms', 'Laundries', 'Smelters', 'Steam tunnels', 'Work in a cold storage (freezer or cool rooms)',
    'Meat packaging and meat storage workers'] },
  { title: 'Work activities undertaken in a compressed air environment', items: [] },
  { title: 'Work activities undertaken by young persons and pregnant women', items: [] },
  { title: 'Work involving strenuous physical demand from manual handling of objects or machinery for prolonged hours. Examples:', groups: [
    { head: 'Repetitive, forceful, or prolonged exertion of hands', items: ['Manual cutting and trimming', 'Manual grinding and polishing'] },
    { head: 'Frequent or heavy lifting, pushing, pulling or carrying of heavy objects', items: ['Tank, truck and ship tanker manual loaders and unloaders', 'Labourers and hand freight, stock, and material movers'] },
    { head: 'Prolonged awkward postures', items: ['Equipment or automotive mechanic and service technicians', 'Work involving manual small parts assembly', 'Work involving installation of overhead electrical wiring'] },
    { head: 'Vibration', items: ['Operating power hand tools (like drills, chippers, jackhammers)'] },
  ] },
  { title: 'Work at elevated heights where workers are liable to fall a distance of more than 3 meters:', items: ['Formwork works', 'Roofing works'] },
]
export const OTE_DECLARATION = 'I confirm that none of the work activities listed above are in MOM’s Annex A, and that none will be undertaken by young persons or pregnant women.'
